import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, Search, GraduationCap } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Learner {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  className: string | null;
  classId: number | null;
}

interface ClassOption {
  id: number;
  name: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const HeadteacherLearners: React.FC = () => {
  const [learners, setLearners] = useState<Learner[]>([]);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  const token = localStorage.getItem("token");

  useAutoDismiss(message, setMessage, null, 5000);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(res.data?.classes || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  }, [token]);

  const fetchLearners = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(pagination.page),
        limit: String(pagination.limit)
      };
      if (searchTerm) params.search = searchTerm;
      if (selectedClass) params.classId = selectedClass;

      const res = await axios.get(getApiUrl("headteacher/learners"), {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setLearners(res.data?.learners || []);
      setPagination(prev => ({
        ...prev,
        total: res.data?.pagination?.total || 0,
        pages: res.data?.pagination?.pages || 0
      }));
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error fetching learners");
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm, selectedClass, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchLearners();
  }, [fetchLearners]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLearners();
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>Learners Overview</h2>
              <p className="page-subtitle">View and manage all learners in your school.</p>
            </div>
          </div>

          {message && (
            <div className="p-4 mb-4 rounded-xl bg-red-50 text-red-700 text-sm">
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <p className="text-sm text-slate-500">Total Learners</p>
              <p className="text-2xl font-bold text-slate-900">{pagination.total}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <p className="text-sm text-slate-500">Current Page</p>
              <p className="text-2xl font-bold text-slate-900">{pagination.page} of {pagination.pages}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <p className="text-sm text-slate-500">Classes</p>
              <p className="text-2xl font-bold text-teal-600">{classes.length}</p>
            </div>
          </div>

          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search by name or email..."
                className="utopia-input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="utopia-input"
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls.id} value={String(cls.id)}>{cls.name}</option>
              ))}
            </select>
            <button type="submit" className="utopia-btn-primary">
              Search
            </button>
          </form>

          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading learners...</div>
          ) : (
            <>
              <table className="utopia-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Class</th>
                    <th>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {learners.map((learner) => (
                    <tr key={learner.id}>
                      <td className="text-slate-500">#{learner.id}</td>
                      <td className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                            <GraduationCap className="text-teal-600" size={16} />
                          </div>
                          {learner.name}
                        </div>
                      </td>
                      <td className="text-slate-600">{learner.email}</td>
                      <td>
                        {learner.className ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                            {learner.className}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="text-slate-500">
                        {new Date(learner.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {learners.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-slate-500 py-8">
                        No learners found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    className="utopia-btn-secondary"
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-600">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    className="utopia-btn-secondary"
                    disabled={pagination.page === pagination.pages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HeadteacherLearners;











